import { useState, useEffect } from 'react';
import { getFaviconPath } from '../utils/commonMethods';

export interface NetworkStatus {
  isOnline: boolean;
  wasOffline: boolean;
}

export const useNetworkStatus = (): NetworkStatus => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      if (!isOnline) {
        setWasOffline(true);
        setIsOnline(true);
        setTimeout(() => {
          setWasOffline(false);
        }, 5000);
      } else {
        setIsOnline(true);
      }
    };

    const handleOffline = () => {
        setIsOnline(false);
    };

    // Enhanced connectivity check using fetch
    const checkConnectivity = async () => {
      const wasOnlineBefore = isOnline;
      
      try {
        const favicon = getFaviconPath();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        const response = await fetch(favicon, {
          method: 'HEAD',
          cache: 'no-cache',
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        const currentlyOnline = navigator.onLine && response.ok;
        if (currentlyOnline && !wasOnlineBefore) {
          setWasOffline(true);
          setIsOnline(true);
          setTimeout(() => {
            setWasOffline(false);
          }, 5000);
        } else if (!currentlyOnline && wasOnlineBefore) {
          setIsOnline(false);
        } else if (currentlyOnline !== isOnline) {
          setIsOnline(currentlyOnline);
        }
      } catch (error) {
        if (wasOnlineBefore) {
          setIsOnline(false);
        }
      }
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // More frequent connectivity check for better responsiveness (every 5 seconds)
    const connectivityInterval = setInterval(checkConnectivity, 5000);

    // Initial connectivity check after a brief delay
    const initialCheck = setTimeout(checkConnectivity, 1000);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(connectivityInterval);
      clearTimeout(initialCheck);
    };
  }, [isOnline]); // Only depend on isOnline to prevent infinite loops

  return { isOnline, wasOffline };
};